/**
 * BottomDock — fixed 5-icon bottom app bar.
 * Theme-aware: uses Tailwind dark: variants throughout.
 * Features:
 *  - Framer Motion spring entrance on mount
 *  - Active pill-shaped highlight behind active icon
 *  - Scale + haptic-style press feedback on each tap
 *  - Badge indicators for unread notifications / pending actions
 *  - Safe-area inset bottom padding for iOS notch/home indicator
 *  - All touch targets ≥ 48×48px
 */
import React from "react";
import { motion } from "motion/react";
import { Home, Users, Calendar, BarChart3, Bell } from "lucide-react";
import { useLocation } from "react-router-dom";

// ── Dock entrance animation ───────────────────────────────────────────────────
const DOCK_VARIANTS = {
  hidden: { y: 120, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 340,
      damping: 32,
      mass: 1,
      delay: 0.15,
    },
  },
};

// ── Badge ─────────────────────────────────────────────────────────────────────
const Badge = ({ count }) => {
  if (!count) return null;
  const label = count > 9 ? "9+" : String(count);
  return (
    <motion.span
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 500, damping: 22 }}
      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5
                 flex items-center justify-center
                 text-[9px] font-black text-white bg-red-500 rounded-full
                 ring-2 ring-white dark:ring-gray-950
                 leading-none z-10"
    >
      {label}
    </motion.span>
  );
};

// ── Single dock item ──────────────────────────────────────────────────────────
const DockItem = ({ icon: Icon, label, isActive, badge, onClick }) => {
  return (
    <motion.button
      whileTap={{ scale: 0.82 }}
      transition={{ type: "spring", stiffness: 600, damping: 28 }}
      onClick={onClick}
      aria-label={label}
      className="relative flex flex-col items-center justify-center gap-1 flex-1 min-h-[48px] py-1 focus:outline-none"
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      {/* Active pill background */}
      {isActive && (
        <motion.div
          layoutId="dock-active-pill"
          className="absolute inset-x-1 top-0.5 bottom-0.5 rounded-2xl
                     bg-orange-500/15 dark:bg-orange-500/15"
          transition={{ type: "spring", stiffness: 420, damping: 34 }}
        />
      )}

      {/* Icon wrapper with badge */}
      <div className="relative flex items-center justify-center w-7 h-7">
        <Icon
          size={22}
          strokeWidth={isActive ? 2.5 : 1.8}
          className={`transition-colors duration-200 ${
            isActive
              ? "text-orange-500 dark:text-orange-400"
              : "text-gray-400 dark:text-gray-500"
          }`}
        />
        <Badge count={badge} />
      </div>

      {/* Label */}
      <span
        className={`text-[10px] font-bold tracking-wide transition-colors duration-200 leading-none ${
          isActive
            ? "text-orange-500 dark:text-orange-400"
            : "text-gray-400 dark:text-gray-600"
        }`}
      >
        {label}
      </span>
    </motion.button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const BottomDock = ({
  activeDrawer,
  onItemPress,
  notifBadge = 0,
  adminBadge = 0,
}) => {
  const location = useLocation();
  const isHome = location.pathname === "/" && !activeDrawer;
  const totalNotifBadge = notifBadge + adminBadge;

  const items = [
    { key: "home", icon: Home, label: "Home", isActive: isHome, badge: 0 },
    {
      key: "teams",
      icon: Users,
      label: "Teams",
      isActive: activeDrawer === "teams",
      badge: 0,
    },
    {
      key: "schedule",
      icon: Calendar,
      label: "Schedule",
      isActive: activeDrawer === "schedule",
      badge: 0,
    },
    {
      key: "stats",
      icon: BarChart3,
      label: "Stats",
      isActive: activeDrawer === "stats",
      badge: 0,
    },
    {
      key: "notifications",
      icon: Bell,
      label: "Alerts",
      isActive: activeDrawer === "notifications",
      badge: totalNotifBadge,
    },
  ];

  return (
    <motion.div
      variants={DOCK_VARIANTS}
      initial="hidden"
      animate="visible"
      className="fixed bottom-0 left-0 right-0 z-30 lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {/* Frosted glass bar — light and dark variants */}
      <div
        className="mx-2 mb-2 rounded-3xl flex items-stretch px-1
                   bg-white/95 dark:bg-gray-950/95
                   backdrop-blur-2xl
                   border border-gray-200 dark:border-white/[0.09]
                   shadow-xl shadow-black/10 dark:shadow-black/60"
      >
        {items.map((item) => (
          <DockItem
            key={item.key}
            icon={item.icon}
            label={item.label}
            isActive={item.isActive}
            badge={item.badge}
            onClick={() => onItemPress(item.key)}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default BottomDock;
